import { makeAutoObservable } from 'mobx'
import { type IIconProps } from 'shared/ui'

export type IFlowActionStoreProps<T = string> = {
  type: T
  edgeType?: string
  name: string
  parentId: string
  childId?: string
  defaultHeight?: number
  defaultWidth?: number
  iconProps?: IIconProps
  id: string
  allowedBeforeActions?: T[]
  allowedNextActions?: T[]
  allowedNextActionsByBranchIndex?: T[][]
}

export class FlowActionStore<T = string> {
  id
  type: T
  edgeType: string
  name: string
  iconProps: IIconProps | null
  parentId: string
  defaultHeight: number
  defaultWidth: number
  allowedBeforeActions: IFlowActionStoreProps<T>['allowedNextActions'] | null
  allowedNextActions: IFlowActionStoreProps<T>['allowedNextActions'] | null
  allowedNextActionsByBranchIndex: T[][] | null

  constructor({
    type,
    edgeType,
    name,
    iconProps,
    parentId,
    childId,
    defaultHeight = 72,
    defaultWidth = 240,
    id,
    allowedBeforeActions,
    allowedNextActions,
    allowedNextActionsByBranchIndex,
  }: IFlowActionStoreProps<T>) {
    this.type = type
    this.edgeType = edgeType || 'customEdge'
    this.name = name
    this.iconProps = iconProps || null
    this.parentId = parentId
    this.defaultHeight = defaultHeight
    this.defaultWidth = defaultWidth
    this.id = id
    this.allowedBeforeActions = allowedBeforeActions || null
    this.allowedNextActions = allowedNextActions || null
    this.allowedNextActionsByBranchIndex = allowedNextActionsByBranchIndex || null
    if (childId) {
      this.addNextId(childId)
    }
    makeAutoObservable(this)
  }

  private _nextIds: string[] = []
  get nextIds() {
    return this._nextIds
  }

  get nextId() {
    if (this.nextIds.length === 1) {
      return this.nextIds[0]
    }
  }
  addNextId = (id: string) => {
    if (this._nextIds.includes(id)) {
      return
    }
    this._nextIds.push(id)
  }
  addNextIdBefore = (id: string) => {
    if (this._nextIds.includes(id)) {
      return
    }
    this._nextIds.splice(this._nextIds.length - 1, 0, id)
  }
  removeNextId = (id: string) => {
    const index = this._nextIds.indexOf(id)
    if (index !== -1) {
      this._nextIds.splice(index, 1)
    }
  }

  replaceNextId = (oldId: string, newId: string) => {
    const index = this._nextIds.indexOf(oldId)
    if (index !== -1) this._nextIds.splice(index, 1, newId)
  }

  setParentId = (id: string) => {
    this.parentId = id
  }

  private _nextErrorsSet: Set<string> = new Set()
  setNextError = (id: string) => {
    this._nextErrorsSet.add(id)
  }

  clearNextErrors = () => {
    this._nextErrorsSet.clear()
  }
  getNextError = (id: string) => {
    return this._nextErrorsSet.has(id)
  }
}
